import React from 'react';
import { TrendingUp, TrendingDown, DollarSign, Users, Activity, Info } from 'lucide-react';
import { CalculatedMetrics } from '../../types';
import {
  gapBuckets,
  priceBuckets,
  marketCapBuckets,
  volumeBuckets,
  analyzeBucketData,
  getGapBucket,
  getPriceBucket,
  getMarketCapBucket,
  getVolumeBucket
} from '../../utils/analysisCalculations';

interface BucketAnalysisGridProps {
  data: CalculatedMetrics[];
  bias: 'long' | 'short';
}

export const BucketAnalysisGrid: React.FC<BucketAnalysisGridProps> = ({ data, bias }) => {
  const gapAnalysisRaw = analyzeBucketData(data, gapBuckets, getGapBucket);
  const priceAnalysisRaw = analyzeBucketData(data, priceBuckets, getPriceBucket);
  const marketCapAnalysisRaw = analyzeBucketData(data, marketCapBuckets, getMarketCapBucket);
  const volumeAnalysisRaw = analyzeBucketData(data, volumeBuckets, getVolumeBucket);

  const gapAnalysis = bias === 'short' ? gapAnalysisRaw.map(item => ({
    ...item,
    redClosePercentage: 100 - item.redClosePercentage,
    greenCloses: item.redCloses,
    redCloses: item.greenCloses
  })) : gapAnalysisRaw;

  const priceAnalysis = bias === 'short' ? priceAnalysisRaw.map(item => ({
    ...item,
    redClosePercentage: 100 - item.redClosePercentage,
    greenCloses: item.redCloses,
    redCloses: item.greenCloses
  })) : priceAnalysisRaw;

  const marketCapAnalysis = bias === 'short' ? marketCapAnalysisRaw.map(item => ({
    ...item,
    redClosePercentage: 100 - item.redClosePercentage,
    greenCloses: item.redCloses,
    redCloses: item.greenCloses
  })) : marketCapAnalysisRaw;

  const volumeAnalysis = bias === 'short' ? volumeAnalysisRaw.map(item => ({
    ...item,
    redClosePercentage: 100 - item.redClosePercentage,
    greenCloses: item.redCloses,
    redCloses: item.greenCloses
  })) : volumeAnalysisRaw;

  const renderBucketTable = (title: string, icon: React.ReactNode, analysis: any[], colorScheme: string) => {
    const maxCount = Math.max(...analysis.map(a => a.count));

    return (
      <div className="premium-chart-card">
        <div className="flex items-center space-x-3 mb-4">
          <div className={`premium-icon-bg ${colorScheme}`}>
            {icon}
          </div>
          <h3 className="premium-chart-title text-base">{title}</h3>
        </div>

        <div className="space-y-2">
          <div className="grid grid-cols-6 gap-2 text-xs font-medium text-gray-400 pb-3 border-b border-gray-700">
            <div className="col-span-2 flex items-center space-x-1">
              <span>Categoria</span>
            </div>
            <div className="text-center flex items-center justify-center space-x-1">
              <span>Quantità</span>
              <Info className="h-3 w-3 opacity-50" title="Numero di gap in questa categoria" />
            </div>
            <div className="text-center flex items-center justify-center space-x-1">
              <span>% Dataset</span>
            </div>
            <div className="text-center flex items-center justify-center space-x-1">
              <span>R/V</span>
              <Info className="h-3 w-3 opacity-50" title="Rosse vs Verdi" />
            </div>
            <div className="text-center flex items-center justify-center space-x-1">
              <span>Bias</span>
              <Info className="h-3 w-3 opacity-50" title="Tendenza predominante" />
            </div>
          </div>

          {analysis.map((item, index) => {
            const widthPercent = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
            const isHighCount = item.count > maxCount * 0.7;

            return (
              <div
                key={index}
                className="grid grid-cols-6 gap-2 text-sm items-center py-3 hover:bg-gray-700/30 rounded transition-colors group"
              >
                <div className="col-span-2 font-medium text-white flex items-center space-x-2">
                  <span>{item.bucket}</span>
                  {isHighCount && (
                    <span className="px-1.5 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded border border-blue-500/30">
                      Frequente
                    </span>
                  )}
                </div>

                <div className="text-center">
                  <div className="relative inline-block min-w-[60px]">
                    <div
                      className="absolute inset-0 bg-blue-500/10 rounded"
                      style={{ width: `${widthPercent}%` }}
                    />
                    <span className="relative z-10 font-semibold text-white px-2 py-1">{item.count}</span>
                  </div>
                </div>

                <div className="text-center">
                  <span className="inline-block px-2 py-1 text-xs font-medium bg-gray-700/50 text-gray-300 rounded">
                    {item.percentageOfTotal.toFixed(1)}%
                  </span>
                </div>

                <div className="text-center">
                  <div className="inline-flex items-center space-x-1 px-2 py-1 rounded bg-gray-800/50">
                    <span className={
                      bias === 'long'
                        ? (item.redCloses > item.greenCloses ? 'text-red-400 font-semibold' : 'text-gray-400')
                        : (item.redCloses > item.greenCloses ? 'text-green-400 font-semibold' : 'text-gray-400')
                    }>
                      {item.redCloses}
                    </span>
                    <span className="text-gray-500 text-xs">/</span>
                    <span className={
                      bias === 'long'
                        ? (item.greenCloses > item.redCloses ? 'text-green-400 font-semibold' : 'text-gray-400')
                        : (item.greenCloses > item.redCloses ? 'text-red-400 font-semibold' : 'text-gray-400')
                    }>
                      {item.greenCloses}
                    </span>
                  </div>
                </div>

                <div className="text-center">
                  <div className="inline-flex items-center space-x-1">
                    {item.redClosePercentage > 60 ? (
                      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded ${
                        bias === 'long'
                          ? 'bg-red-500/10 border border-red-500/30'
                          : 'bg-green-500/10 border border-green-500/30'
                      }`}>
                        {bias === 'long' ? (
                          <TrendingDown className="h-3 w-3 text-red-400" />
                        ) : (
                          <TrendingUp className="h-3 w-3 text-green-400" />
                        )}
                        <span className={bias === 'long' ? 'text-red-400 font-semibold text-xs' : 'text-green-400 font-semibold text-xs'}>
                          {bias === 'long' ? 'SHORT' : 'LONG'}
                        </span>
                      </span>
                    ) : item.redClosePercentage < 40 ? (
                      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded ${
                        bias === 'long'
                          ? 'bg-green-500/10 border border-green-500/30'
                          : 'bg-red-500/10 border border-red-500/30'
                      }`}>
                        {bias === 'long' ? (
                          <TrendingUp className="h-3 w-3 text-green-400" />
                        ) : (
                          <TrendingDown className="h-3 w-3 text-red-400" />
                        )}
                        <span className={bias === 'long' ? 'text-green-400 font-semibold text-xs' : 'text-red-400 font-semibold text-xs'}>
                          {bias === 'long' ? 'LONG' : 'SHORT'}
                        </span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center space-x-1 px-2 py-1 bg-gray-500/10 border border-gray-500/30 rounded">
                        <span className="text-gray-400 font-medium text-xs">NEUTRO</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {renderBucketTable(
        'Analisi per Gap %',
        <Activity className="h-5 w-5 text-blue-400" />,
        gapAnalysis,
        ''
      )}

      {renderBucketTable(
        'Analisi per Prezzo Open',
        <DollarSign className="h-5 w-5 text-green-400" />,
        priceAnalysis,
        'success'
      )}

      {renderBucketTable(
        'Analisi per Market Cap',
        <Users className="h-5 w-5 text-yellow-400" />,
        marketCapAnalysis,
        'warning'
      )}

      {renderBucketTable(
        'Analisi per Volume PM',
        <TrendingUp className="h-5 w-5 text-purple-400" />,
        volumeAnalysis,
        'error'
      )}
    </div>
  );
};
